import store from "./store";
import AWSAppSyncClient from "aws-appsync";
import * as awsVars from '../../config/aws-exports';
let AWSConfig = awsVars.default(process.env);
import Auth from '@aws-amplify/auth';
import gql from "graphql-tag";
import * as Queries from '../../graphql/queries';
import * as Mutations from '../../graphql/mutations';
import Blackbox from "./blackbox";
import {toast} from "@/plugins/toast";
import {EventBus} from "./event-bus";

const defaultOptions = {
	watchQuery: {
		fetchPolicy: 'no-cache',
		errorPolicy: 'ignore'
	},
	query: {
		fetchPolicy: 'no-cache',
		errorPolicy: 'all'
	}
};

/**
 * Helper function to get current path for redirect
 */
function getCurrentPath() {
	try {
		return window.location.pathname + window.location.search;
	} catch (e) {
		return '/vaults';
	}
}

/**
 * Helper function to emit auth required event
 */
function emitAuthRequiredFromToken(reason) {
	EventBus.$emit('authState', {
		status: 'needAuthentication',
		redirect: getCurrentPath(),
		reason: reason || 'Your session has expired. Please sign in again.'
	});
}

let appSyncClientSingleton = new AWSAppSyncClient({
	// Disable the offline cache. The app uses fetchPolicy 'no-cache' for all
	// queries, so the offline store provides no benefit - but its persisted
	// Apollo/Redux cache must rehydrate on every page load, and on Safari that
	// rehydration returns an undefined cache root, making EVERY query fail with
	// "Can't find field <name> on object undefined". That broke getUserById on
	// refresh and bounced users to the login page. See SC ticket / Safari logout.
	disableOffline: true,
	url: AWSConfig.aws_appsync_graphqlEndpoint,
	region: AWSConfig.aws_appsync_region,
	auth: {
		type: AWSConfig.aws_appsync_authenticationType,
		jwtToken: async function() {
			try {
				const session = await Auth.currentSession();
				return session.getIdToken().getJwtToken();
			} catch (err) {
				console.error('Failed to get JWT token:', err);
				// Check if this is a "no current user" or session expired error
				const errorMessage = err && err.message ? err.message.toLowerCase() : '';
				if (errorMessage.indexOf('no current user') !== -1 || 
				    errorMessage.indexOf('expired') !== -1 ||
				    errorMessage.indexOf('not authenticated') !== -1) {
					emitAuthRequiredFromToken('Your session has expired. Please sign in again.');
				}
				// CRITICAL: never reject/throw here. aws-appsync-auth-link's authLink calls
				// `promise.then(observable => observable.subscribe(...))` with NO onRejected
				// handler and NO .catch() - if this promise rejects, the request's Observable
				// never emits next/error/complete, so the GraphQL call hangs forever (silently,
				// with only an "Uncaught (in promise)" in the console). That hang is what made
				// signature-request pages spin forever and made stale-tab uploads look like they
				// "succeeded" (the file went to S3 but the follow-up DB-record mutation just hung).
				// Resolving with an empty token instead lets the request go out and fail normally
				// through AppSync's standard Unauthorized error, which _appSyncCall already knows
				// how to handle (retry-once / surface an error to the caller).
				return '';
			}
		}
	},
	defaultOptions
});

export default class BaseService {
	store = store;
	appSyncClient = appSyncClientSingleton;
	blackbox = new Blackbox("DEBUG");

	_checkVuexCache(path) {
		return !!this._deepGet(path);
	}

	_deepSet(obj, path, value) {
		let schema = obj;  // a moving reference to internal objects within obj
		let pList = path.split('.');
		let len = pList.length;
		for (let i = 0; i < len - 1; i++) {
			let elem = pList[i];
			if (!schema[elem]) schema[elem] = {};
			schema = schema[elem];
		}

		schema[pList[len - 1]] = value;
	}

	_deepGetCacheExpired(path) {
		let expirationDateExpired = true;
		if (this.store.state.cache && this.store.state.cache.hasOwnProperty(path)) {
			let nowDate = new Date();
			if (this.store.state.cache[path] > nowDate) {
				expirationDateExpired = false;
			}
		}
		return expirationDateExpired;
	}

	_deepGet(path, collection) {
		let schema = typeof collection !== "undefined" ? collection : this.store.state;  // a moving reference to internal objects within obj
		let pList = path.split('.');
		let len = pList.length;
		for (let i = 0; i < len - 1; i++) {
			let elem = pList[i];
			if (!schema[elem]) schema[elem] = {};
			schema = schema[elem];
		}

		return schema[pList[len - 1]];
	}

	/**
	 * Detects if an error is an authentication/authorization error that should trigger
	 * a session refresh attempt or redirect to login.
	 * @param {Error} err - The error object to check
	 * @returns {boolean} - True if this is an auth-related error
	 */
	_isAuthenticationError(err) {
		if (!err) {
			return false;
		}

		// Check for auth-related keywords in the error message
		const authKeywords = ['unauthorized', 'not authorized', 'expired', 'access denied', 'forbidden', 'authentication', 'no current user'];
		
		// Helper to check if a string contains any auth keywords
		const containsAuthKeyword = function(str) {
			if (!str || typeof str !== 'string') {
				return false;
			}
			const lowerStr = str.toLowerCase();
			for (let i = 0; i < authKeywords.length; i++) {
				if (lowerStr.indexOf(authKeywords[i]) !== -1) {
					return true;
				}
			}
			return false;
		};

		// Check graphQLErrors for auth-related error types
		if (err.graphQLErrors && err.graphQLErrors.length > 0) {
			for (let i = 0; i < err.graphQLErrors.length; i++) {
				let gqlError = err.graphQLErrors[i];
				
				// Check errorType field
				if (gqlError && gqlError.errorType) {
					const errorType = gqlError.errorType.toLowerCase();
					if (errorType.indexOf('unauthorized') !== -1 || 
					    errorType.indexOf('unauthenticated') !== -1 ||
					    errorType.indexOf('forbidden') !== -1) {
						return true;
					}
				}
				
				// Check error message
				if (gqlError && gqlError.message && containsAuthKeyword(gqlError.message)) {
					return true;
				}
			}
		}

		// Check the main error message
		if (err.message && containsAuthKeyword(err.message)) {
			return true;
		}

		// Check for network errors that might be auth-related
		if (err.networkError) {
			// 401 Unauthorized or 403 Forbidden status codes
			if (err.networkError.statusCode === 401 || err.networkError.statusCode === 403) {
				return true;
			}
			if (err.networkError.message && containsAuthKeyword(err.networkError.message)) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Emits the needAuthentication event to trigger a redirect to login
	 * @param {string} reason - The reason message to display to the user
	 */
	_emitAuthRequired(reason) {
		let currentPath = '/vaults';
		try {
			currentPath = window.location.pathname + window.location.search;
		} catch (e) {
			// Fallback to default path if window is not available
		}
		
		EventBus.$emit('authState', {
			status: 'needAuthentication',
			redirect: currentPath,
			reason: reason || 'Your session has expired. Please sign in again.'
		});
	}

	_appSyncCall(callType, name, parameters, path, options) {
		return new Promise((resolve, reject) => {
			if (typeof parameters === "string" && typeof path === "undefined") {
				path = parameters;
				parameters = undefined;
			}
			let self = this;

			let appSyncCachePolicy = "no-cache";
			let vuexCache = true;

			if (options) {
				if (options.hasOwnProperty("appSyncCache")) {
					appSyncCachePolicy = options.appSyncCache ? "cache-first" : "no-cache";
				}

				if (options.hasOwnProperty("vuexCache")) {
					vuexCache = !!options.vuexCache;
				}
			}

			let gqlCommand = null;
			let commandOptions = {
				fetchPolicy: appSyncCachePolicy,
				variables: parameters
			};

			let cachedResult = null;
			let haveCachedResult = false;
			if (path && callType === "query" && vuexCache && this._checkVuexCache(path)) {
				if (!this._deepGetCacheExpired(path)) {
					cachedResult = this._deepGet(path);
					haveCachedResult = true;
				}
			}

			if (haveCachedResult) {
				//this.debug(`AppSync Cached Result for: ${name} => ` + JSON.stringify(cachedResult));
				resolve(cachedResult);
			} else {
				if (callType === "query" && Queries[name]) {
					gqlCommand = Queries[name];
					commandOptions.query = gql(gqlCommand);
				} else if (callType === "mutate" && Mutations[name]) {
					gqlCommand = Mutations[name];
					commandOptions.mutation = gql(gqlCommand);
				}

				if (!gqlCommand) {
					console.error("Invalid request name: " + name);
					reject(new Error("Invalid request name: " + name));
				} else {
					//Check Vuex Cache?
					//this.log("Calling AppSync: " + name);

					self.appSyncClient[callType](commandOptions).then((commandResponse) => {
						let finalResult = null;
						if (commandResponse && commandResponse.data && commandResponse.data.hasOwnProperty(name)) {
							finalResult = commandResponse.data[name];
						} else if (commandResponse && commandResponse.data) {
							finalResult = commandResponse.data;
						} else {
							finalResult = commandResponse;
						}

						//this.debug(`AppSync Result for: ${name} => ` + JSON.stringify(finalResult));

						if (path) {
							self.store.commit(path, finalResult);
							let expirationDate = new Date();
							expirationDate.setTime(expirationDate.getTime() + (60 * 60 * 1000));
							self.store.state.cache[path] = expirationDate;
							//this.debug(`AppSync Result for: ${name} committed to store => ${path}`);
						}

						resolve(finalResult);
		}).catch((err) => {
			// Log the GraphQL operation details for debugging
			console.error(`==========================================`);
			console.error(`GraphQL ${callType.toUpperCase()} Error: ${name}`);
			console.error(`Parameters:`, parameters);
			console.error(`Path:`, path || 'none');
			console.error(`==========================================`);
			
			// Debug: Log error object structure
			console.log(`[DEBUG ${name}] Error caught in _appSyncCall`);
			console.log(`[DEBUG ${name}] Error type:`, typeof err);
			console.log(`[DEBUG ${name}] Error constructor:`, err && err.constructor ? err.constructor.name : 'unknown');
			console.log(`[DEBUG ${name}] Has graphQLErrors:`, !!(err && err.graphQLErrors));
			console.log(`[DEBUG ${name}] graphQLErrors length:`, err && err.graphQLErrors ? err.graphQLErrors.length : 0);
			
			// Check if this is an authentication error
			const isAuthError = self._isAuthenticationError(err);
			const isRetry = options && options._isRetry;
			
			console.log(`[DEBUG ${name}] Is auth error:`, isAuthError);
			console.log(`[DEBUG ${name}] Is retry attempt:`, isRetry);
			
			// If this is an auth error and we haven't already retried, attempt token refresh
			if (isAuthError && !isRetry) {
				console.log(`[DEBUG ${name}] Auth error detected, attempting session refresh...`);
				
				Auth.currentSession().then(function(session) {
					console.log(`[DEBUG ${name}] Session refresh successful, retrying request...`);
					
					// Create new options with retry flag to prevent infinite loops
					const retryOptions = options ? Object.assign({}, options, { _isRetry: true }) : { _isRetry: true };
					
					// Retry the original request
					self._appSyncCall(callType, name, parameters, path, retryOptions)
						.then(function(result) {
							resolve(result);
						})
						.catch(function(retryErr) {
							console.error(`[DEBUG ${name}] Retry failed after session refresh:`, retryErr.message);
							reject(retryErr);
						});
				}).catch(function(refreshError) {
					// Session refresh failed - redirect to login
					console.error(`[DEBUG ${name}] Session refresh failed:`, refreshError);
					console.error(`[DEBUG ${name}] Redirecting to login...`);
					
					self._emitAuthRequired('Your session has expired. Please sign in again.');
					
					const sessionError = new Error('Session expired');
					sessionError.isAuthError = true;
					reject(sessionError);
				});
				
				return; // Exit early - we're handling this asynchronously
			}
			
			// Helper function to safely extract string from any value
			const safeStringify = function(value, fallback) {
				if (value === null || value === undefined) {
					return fallback;
				}
				if (typeof value === 'string') {
					return value;
				}
				try {
					// Try toString first (safer than JSON.stringify)
					if (value.toString && typeof value.toString === 'function' && value.toString !== Object.prototype.toString) {
						const str = value.toString();
						if (str !== '[object Object]') {
							console.log(`[DEBUG ${name}] toString succeeded:`, str.substring(0, 100));
							return str;
						}
					}
					// Last resort: try JSON.stringify
					console.log(`[DEBUG ${name}] Attempting JSON.stringify...`);
					const result = JSON.stringify(value);
					console.log(`[DEBUG ${name}] JSON.stringify succeeded`);
					return result;
				} catch (e) {
					console.log(`[DEBUG ${name}] Stringify failed:`, e.message);
					return fallback;
				}
			};
			
			// Create a sanitized error object without circular references
			let sanitizedError = null;
			
			if (err && err.graphQLErrors && err.graphQLErrors.length > 0) {
				console.log(`[DEBUG ${name}] Processing graphQLErrors...`);
				const errors = [];
				for (let e = 0; e < err.graphQLErrors.length; e++) {
					let gqlError = err.graphQLErrors[e];
					console.log(`[DEBUG ${name}] Processing error ${e}:`, typeof gqlError);
					console.log(`[DEBUG ${name}] Error has message:`, !!(gqlError && gqlError.message));
					console.log(`[DEBUG ${name}] Message type:`, gqlError && gqlError.message ? typeof gqlError.message : 'none');
					
					const errorMessage = safeStringify(
						gqlError && gqlError.message ? gqlError.message : null,
						'Unknown GraphQL error'
					);
					
					let errorPath = 'none';
					try {
						errorPath = gqlError && gqlError.path && Array.isArray(gqlError.path) ? gqlError.path.join('/') : 'none';
					} catch (e) {
						errorPath = 'unknown';
					}
					
					console.error(`GQL ${name} Error: ${errorMessage} [${errorPath}]`);
					
					errors.push({
						message: errorMessage,
						path: errorPath
					});
				}
				
				console.log(`[DEBUG ${name}] Creating sanitized error from graphQLErrors`);
				sanitizedError = new Error(`GraphQL error: ${errors[0].message}`);
				sanitizedError.graphQLErrors = errors;
			} else {
				console.log(`[DEBUG ${name}] No graphQLErrors, processing general error...`);
				console.log(`[DEBUG ${name}] Error has message property:`, !!(err && err.message));
				console.log(`[DEBUG ${name}] Error message type:`, err && err.message ? typeof err.message : 'none');
				
				// Safely log error without circular references
				const errorMsg = safeStringify(
					err && err.message ? err.message : err,
					'Unknown error occurred'
				);
				
				console.error("Unable to process GQL request: " + errorMsg);
				
				console.log(`[DEBUG ${name}] Creating sanitized error from general error`);
				sanitizedError = new Error(errorMsg);
			}

			console.log(`[DEBUG ${name}] Rejecting with sanitized error:`, sanitizedError.message);
			console.error(`========== End ${name} Error ==========`);
			reject(sanitizedError);
		});
				}
			}
		});
	}

	query(name, parameters, path, options) {
		return this._appSyncCall("query", name, parameters, path, options);
	}

	mutation(name, parameters, path, options) {
		return this._appSyncCall("mutate", name, parameters, path, options);
	}

	_log(message) {
		this.blackbox.writeLog(message)
	}

	_info(message) {
		this.blackbox.writeInfo(message)
	}

	_warn(message) {
		this.blackbox.writeWarn(message)
	}

	_error(message) {
		this.blackbox.writeError(message)
	}

	_fatal(message) {
		this.blackbox.writeFatal(message)
	}

	_debug(message) {
		this.blackbox.writeDebug(message)
	}

	_trace(message) {
		this.blackbox.writeTrace(message)
	}

	notification(mode, message, shy) {
		let backgroundColor = "rgba(86,124,206," + (shy ? '.5' : '1') + ")";

		if (mode === "success") {
			backgroundColor = "rgba(39,174,96," + (shy ? '.5' : '1') + ")";
		} else if (mode === "error") {
			backgroundColor = "rgba(192,57,43," + (shy ? '.5' : '1') + ")";
		}

		const options = {
			style: {
				main: {
					background: backgroundColor,
					color: "white"
				}
			},
			settings: {
				duration: shy ? 1000 : 5000
			}
		};

		toast.toast(message, options);
	}
}


